
import React, { useState } from 'react';
import { useAccountingReport } from './useAccountingReport';
import { useAuth } from '../../context/AuthContext';
import moment from 'moment';
import DetalleCuentaModal from './DetalleCuentaModal';
import ExportButton from '../common/ExportButton';
import { exportToCSV, exportToExcel, exportToPDF } from '../../utils/exportUtils';

// Types mapping
// eslint-disable-next-line no-unused-vars
const ACCOUNT_TYPES = {
    'ACTIVO': 'Activos',
    'PASIVO': 'Pasivos',
    'CAPITAL': 'Capital',
};

// Nature mapping
// eslint-disable-next-line no-unused-vars
const NATURE_MULTIPLIER = {
    'DEUDORA': 1,   // Positive Balance = Debit > Credit
    'ACREEDORA': -1 // Positive Balance = Credit > Debit (so we invert the Debit-Credit val)
};

function BalanceGeneral({ month, year }) {
    const { userData } = useAuth();

    // End Date is end of selected month (Cumulative view)
    const endDate = React.useMemo(() =>
        moment().year(year).month(month).endOf('month').toDate(),
        [year, month]);

    // Start date is beginning of time for Balance Sheet (cumulative)
    // using a far past date
    const startDate = React.useMemo(() => new Date('2020-01-01'), []);

    const { loading, accounts, balances, ledgerEntries } = useAccountingReport(
        userData?.condominioId,
        startDate,
        endDate
    );

    // Modal State
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [selectedEntries, setSelectedEntries] = useState([]);

    const handleAccountClick = (account) => {
        const relevantMovements = [];

        // Helper to check if an account ID belongs to Income or Expense types
        const isPnLAccount = (id) => {
            const acc = accounts.find(a => a.id === id);
            return acc && (acc.tipo === 'INGRESO' || acc.tipo === 'GASTO');
        };

        ledgerEntries.forEach(asiento => {
            if (asiento.detalles) {
                asiento.detalles.forEach(detalle => {
                    let match = false;
                    if (account.id === 'generated_earnings') {
                        // Match ANY Income or Expense account
                        if (isPnLAccount(detalle.cuentaId)) {
                            match = true;
                        }
                    } else {
                        // Exact match
                        if (detalle.cuentaId === account.id) {
                            match = true;
                        }
                    }

                    if (match) {
                        relevantMovements.push({
                            ...detalle,
                            fecha: asiento.fecha,
                            concepto: asiento.concepto,
                            tipoReferencia: asiento.tipoReferencia,
                            referenciaId: asiento.referenciaId
                        });
                    }
                });
            }
        });
        setSelectedAccount(account);
        setSelectedEntries(relevantMovements);
    };

    // Grouping
    const grouped = {
        'ACTIVO': [],
        'PASIVO': [],
        'CAPITAL': []
    };

    let totalActivo = 0;
    let totalPasivo = 0;
    let totalCapital = 0;
    let totalIngresos = 0;
    let totalGastos = 0;

    if (!loading) {
        accounts.forEach(acc => {
            let rawBalance = balances[acc.id] || 0;

            // LOGIC:
            // 1. ASSET (Activo) [Deudora]: Positive = Debit Balance.
            // 2. LIABILITY (Pasivo) [Acreedora]: Positive = Credit Balance.
            // 3. EQUITY (Capital) [Acreedora]: Positive = Credit Balance.
            // 4. INCOME (Ingreso) [Acreedora]: Positive = Credit Balance.
            // 5. EXPENSE (Gasto) [Deudora]: Positive = Debit Balance.

            if (acc.tipo === 'ACTIVO') {
                // Presentation: Positive
                if (Math.abs(rawBalance) > 0.01) {
                    grouped['ACTIVO'].push({ ...acc, balance: rawBalance });
                    totalActivo += rawBalance;
                }
            } else if (acc.tipo === 'PASIVO') {
                // Presentation: Positive if Credit > Debit (rawBalance negative)
                let pres = rawBalance * -1;
                if (Math.abs(pres) > 0.01) {
                    grouped['PASIVO'].push({ ...acc, balance: pres });
                    totalPasivo += pres;
                }
            } else if (acc.tipo === 'CAPITAL') {
                let pres = rawBalance * -1;
                if (Math.abs(pres) > 0.01) {
                    grouped['CAPITAL'].push({ ...acc, balance: pres });
                    totalCapital += pres;
                }
            } else if (acc.tipo === 'INGRESO') {
                // For detailed P&L calculation (hidden in BS, but needed for Equity)
                // Income is Credit nature -> rawBalance is usually negative
                totalIngresos += (rawBalance * -1);
            } else if (acc.tipo === 'GASTO') {
                // Expense is Debit nature -> rawBalance is usually positive
                totalGastos += rawBalance;
            }
        });
    }

    // --- DYNAMIC RETAINED EARNINGS (RESULTADO DEL EJERCICIO) ---
    // Assets = Liab + Capital + (Income - Expense)
    const currentEarnings = totalIngresos - totalGastos;

    // Look for account 305.01 in the list to match exactly
    const officialEarningsAcc = accounts.find(a => a.numero === '305.01');
    const earningsCode = officialEarningsAcc ? officialEarningsAcc.numero : '305.01';
    const earningsName = officialEarningsAcc ? officialEarningsAcc.nombre : 'Resultado del Ejercicio (Actual)';

    // Add to Capital Group for presentation
    if (Math.abs(currentEarnings) > 0.01) {
        grouped['CAPITAL'].push({
            id: 'generated_earnings',
            numero: earningsCode,
            nombre: earningsName,
            tipo: 'CAPITAL',
            balance: currentEarnings,
            isComputed: false // Allow drill-down
        });
        totalCapital += currentEarnings;
    }

    // Sort by code
    Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => a.numero.localeCompare(b.numero));
    });

    // Verification Equation
    const isBalanced = Math.abs(totalActivo - (totalPasivo + totalCapital)) < 1.0;

    if (loading) return <div>Cargando Balance General...</div>;

    // Export Logic
    const getExportData = () => {
        const data = [];

        // Helper to add section
        const addSection = (title, items) => {
            data.push({ Numero: title, Nombre: '', Balance: '' }); // Header
            items.forEach(item => {
                data.push({
                    Numero: item.numero,
                    Nombre: item.nombre,
                    Balance: item.balance
                });
            });
        };

        addSection('ACTIVOS', grouped['ACTIVO']);
        addSection('PASIVOS', grouped['PASIVO']);
        addSection('CAPITAL', grouped['CAPITAL']);

        return data;
    };

    const handleExportCSV = () => {
        exportToCSV(getExportData(), `Balance_General_${month + 1}_${year}`);
    };

    const handleExportExcel = () => {
        exportToExcel(getExportData(), `Balance_General_${month + 1}_${year}`, 'Balance General', userData?.condominioNombre);
    };

    const handleExportPDF = () => {
        const columns = ['Número', 'Cuenta', 'Balance'];
        const rows = [];

        // Formatting for PDF
        const addSectionToPDF = (title, items, total) => {
            rows.push([{ content: title, colSpan: 3, styles: { fillColor: [240, 240, 240], fontStyle: 'bold' } }]);
            items.forEach(item => {
                rows.push([item.numero, item.nombre, `$${item.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}`]);
            });
            rows.push([{ content: `TOTAL ${title}`, colSpan: 2, styles: { fontStyle: 'bold' } }, { content: `$${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, styles: { fontStyle: 'bold' } }]);
        };

        addSectionToPDF('ACTIVO', grouped['ACTIVO'], totalActivo);
        addSectionToPDF('PASIVO', grouped['PASIVO'], totalPasivo);
        addSectionToPDF('CAPITAL', grouped['CAPITAL'], totalCapital);

        exportToPDF(
            `Balance General - ${month + 1}/${year}`,
            columns,
            rows,
            `Balance_General_${month + 1}_${year}`,
            userData?.condominioNombre
        );
    };

    const renderRow = (acc) => (
        <tr
            key={acc.id}
            style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', transition: 'background 0.2s' }}
            onClick={() => handleAccountClick(acc)}
            className="report-row"
        >
            <td style={{ padding: '8px', color: '#666' }}>{acc.numero}</td>
            <td style={{ padding: '8px' }}>{acc.nombre}</td>
            <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                ${acc.balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </td>
        </tr>
    );

    return (
        <div className="report-container">
            {selectedAccount && (
                <DetalleCuentaModal
                    account={selectedAccount}
                    entries={selectedEntries}
                    onClose={() => setSelectedAccount(null)}
                />
            )}

            {/* Header handled by Parent */}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                <ExportButton
                    onExportCSV={handleExportCSV}
                    onExportExcel={handleExportExcel}
                    onExportPDF={handleExportPDF}
                />
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, padding: '15px', background: '#ecfdf5', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
                    <h4>Total Activo</h4>
                    <p style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#047857' }}>${new Intl.NumberFormat('es-MX').format(totalActivo.toFixed(2))}</p>
                </div>
                <div style={{ flex: 1, padding: '15px', background: '#fff7ed', borderRadius: '8px', borderLeft: '4px solid #f97316' }}>
                    <h4>Total Pasivo</h4>
                    <p style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#c2410c' }}>${new Intl.NumberFormat('es-MX').format(totalPasivo.toFixed(2))}</p>
                </div>
                <div style={{ flex: 1, padding: '15px', background: '#eff6ff', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                    <h4>Total Capital</h4>
                    <p style={{ fontSize: '1.5em', fontWeight: 'bold', color: '#1d4ed8' }}>${new Intl.NumberFormat('es-MX').format(totalCapital.toFixed(2))}</p>
                </div>
            </div>

            {!isBalanced && (
                <div style={{ padding: '10px', background: '#fee2e2', color: '#ef4444', borderRadius: '4px', marginBottom: '20px', fontWeight: 'bold', textAlign: 'center' }}>
                    ⚠️ El balance no cuadra. Diferencia: ${new Intl.NumberFormat('es-MX').format(totalActivo - (totalPasivo + totalCapital))}
                </div>
            )}

            <div style={{ display: 'flex', gap: '40px' }}>
                {/* Left Column: Assets */}
                <div style={{ flex: 1 }}>
                    <h3 style={{ borderBottom: '2px solid #ddd', paddingBottom: '10px' }}>ACTIVOS</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                        <tbody>
                            {grouped['ACTIVO'].map(acc => renderRow(acc))}
                            {grouped['ACTIVO'].length === 0 && <tr><td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No hay cuentas de activo</td></tr>}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f9fafb' }}>
                                <td colSpan="2" style={{ padding: '10px', fontWeight: 'bold' }}>TOTAL ACTIVO</td>
                                <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>${new Intl.NumberFormat('es-MX').format(totalActivo.toFixed(2))}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* Right Column: Liabilities & Equity */}
                <div style={{ flex: 1 }}>
                    <h3 style={{ borderBottom: '2px solid #ddd', paddingBottom: '10px' }}>PASIVOS</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px', marginBottom: '30px' }}>
                        <tbody>
                            {grouped['PASIVO'].map(acc => renderRow(acc))}
                            {grouped['PASIVO'].length === 0 && <tr><td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No hay cuentas de pasivo</td></tr>}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f9fafb' }}>
                                <td colSpan="2" style={{ padding: '10px', fontWeight: 'bold' }}>TOTAL PASIVO</td>
                                <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>${new Intl.NumberFormat('es-MX').format(totalPasivo.toFixed(2))}</td>
                            </tr>
                        </tfoot>
                    </table>

                    <h3 style={{ borderBottom: '2px solid #ddd', paddingBottom: '10px' }}>CAPITAL</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '10px' }}>
                        <tbody>
                            {grouped['CAPITAL'].map(acc => renderRow(acc))}
                            {grouped['CAPITAL'].length === 0 && <tr><td colSpan="3" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No hay cuentas de capital</td></tr>}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: '#f9fafb' }}>
                                <td colSpan="2" style={{ padding: '10px', fontWeight: 'bold' }}>TOTAL CAPITAL</td>
                                <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>${new Intl.NumberFormat('es-MX').format(totalCapital.toFixed(2))}</td>
                            </tr>
                        </tfoot>
                    </table>

                    <div style={{ marginTop: '20px', padding: '10px', background: '#f3f4f6', borderRadius: '4px', textAlign: 'right' }}>
                        <strong>Total Pasivo + Capital: </strong>
                        ${new Intl.NumberFormat('es-MX').format((totalPasivo + totalCapital).toFixed(2))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BalanceGeneral;
